/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, notification, Select, Tooltip, Upload } from 'antd';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Button as CrceButton } from '@crc-evans/ui-components';
import {
  Eye,
  Pencil,
  Trash2,
  FileText,
  Download,
  Upload as UploadIcon,
  Plus,
  Truck,
  Search,
  X,
} from 'lucide-react';
import AddNewShipments from './AddNewShipments';
import ShipmentLogs from './ShipmentLogs';
import { fetchShipmentTypeDetails } from '../../redux/ShipmentDetails/actions';
import { fetchShipmentDashBoardDetails } from '../../redux/ShipmentDashboardDetails/actions';
import { OnDeleteShipments } from '../../redux/ShipmentDashboardDetails/saga';
import axiosConfig from '../../redux/ShipmentDashboardDetails/axiosConfig';
import axios from 'axios';
import { AWS_IMAGE_URL } from '../../config';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
} from './shipmentFieldOptions';
import { getProjectTableLoadingConfig } from '../ProjectTableLoader';
import './ShipmentDashBoardPage.css';

const { Option } = Select;

const resolveUserGroups = (userAccessList) => {
  const fromProps = Array.isArray(userAccessList) ? userAccessList : [];
  if (fromProps.length > 0) return fromProps;
  try {
    const raw = localStorage.getItem('UserGroup');
    if (raw == null || raw === '') return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } catch {
    return [];
  }
};

const isGlobalAdminGroup = (groups) =>
  Array.isArray(groups) &&
  groups.some((g) => {
    const n = String(g || '').toLowerCase();
    return n === 'admin' || n === 'super-admin' || n === 'administrator';
  });

/** Explicit false => no write; missing tab key => allow (legacy). */
const hasShipmentTabWrite = (projectTeamMembersAccessList) => {
  const tabs = projectTeamMembersAccessList?.[0]?.tabPermissions;
  if (!tabs || typeof tabs !== 'object') return true;
  const w = tabs.Shipment?.write ?? tabs['Shipment']?.write ?? tabs.shipment?.write;
  if (w === false) return false;
  return true;
};

const ShipmentDashBoardPage = (props) => {
  const {
    selectedProject,
    onGetShipmentTypes,
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    OnGetShipmentsData,
    userAccessList,
    projectTeamMembersAccessList,
  } = props;

  const [projectIdValue, setProjectIdValue] = useState(false);

  const [showAddModel, setShowAddModel] = useState(false);
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [editRecord, setEditRecord] = useState('');
  const [isEdit, setIsEdit] = useState('');
  const [adminShipmentTypesList, setAdminShipmentTypesList] = useState([]);

  const [currentPagesize, setCurrentPagesize] = useState(10);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isFileModalVisible, setIsFileModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkFileList, setBulkFileList] = useState([]);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [searchKeyExist, setSearchKeyExist] = useState('');

  const [showFormModel, setShowFormModel] = useState(false);

  const [current, setCurrent] = useState(1);

  useEffect(() => {
    if (adminShipmentTypesList && adminShipmentTypesList.length == 0) onGetShipmentTypes();
  }, []);

  useEffect(() => {
    // console.log('ShipmentTypes:', shipmentsTypes.length);

    if (shipmentsTypes && shipmentsTypes.length > 0) setAdminShipmentTypesList(shipmentsTypes);
  }, [shipmentsTypes]);

  /** true = user cannot edit/delete shipments */
  const isShipmentReadOnly = useCallback(() => {
    const groups = resolveUserGroups(userAccessList);
    if (isGlobalAdminGroup(groups)) return false;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return true;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !hasShipmentTabWrite(projectTeamMembersAccessList)
    ) {
      return true;
    }
    return false;
  }, [userAccessList, projectTeamMembersAccessList]);

  const renderStatusDot = (record, formType) => {
    const isActive =
      record.submissions && record.submissions.some((item) => item.formType === formType);

    return (
      <Tooltip title={isActive ? 'Form submitted' : 'Form not submitted'}>
        <span className={`ship-status-dot ${isActive ? 'is-success' : 'is-danger'}`}>
          <span className="ship-status-dot-inner" aria-hidden />
        </span>
      </Tooltip>
    );
  };

  /** Setting coloumn (headers) for Equipment table */
  const columns = useMemo(
    () => [
      {
        // title: 'PIH Equipment',
        dataIndex: 'sno',
        key: 'sno',
        align: 'center',
        width: '55px',
        render: (text, record, index) => {
          return (current - 1) * currentPagesize + (index + 1);
        },
      },
      {
        title: 'Shipment Type',
        dataIndex: 'shipmentTypeName',
        key: 'shipmentTypeName',
        align: 'left',
        ellipsis: {
          showTitle: false,
        },
      },

      {
        title: 'Shipment Number',
        dataIndex: 'shipmentNumber',
        key: 'shipmentNumber',
        align: 'center',
      },
      {
        title: 'Priority',
        key: 'priority',
        dataIndex: 'priority',
        width: 100,
        align: 'center',
        ellipsis: true,
        render: (_, record) => {
          const rawP = record?.priority ?? record?.Priority ?? record?.shipmentPriority;
          if (rawP == null || String(rawP).trim() === '') return '—';
          const meta = normalizeShipmentMetaFromRecord(record);
          const opt = SHIPMENT_PRIORITY_OPTIONS.find((o) => o.value === meta.priority);
          return opt ? opt.label : String(rawP);
        },
      },
      {
        title: 'Category',
        key: 'shipmentCategory',
        dataIndex: 'shipmentCategory',
        width: 140,
        align: 'center',
        ellipsis: true,
        render: (_, record) => {
          const rawC =
            record?.shipmentCategory ?? record?.shipment_category ?? record?.category ?? record?.Category;
          if (rawC == null || String(rawC).trim() === '') return '—';
          const meta = normalizeShipmentMetaFromRecord(record);
          const opt = SHIPMENT_CATEGORY_OPTIONS.find((o) => o.value === meta.shipmentCategory);
          return opt ? opt.label : String(rawC);
        },
      },
      {
        title: 'Subcategory',
        key: 'packagingSubcategory',
        dataIndex: 'packagingType',
        width: 140,
        align: 'center',
        ellipsis: true,
        render: (_, record) => {
          const raw =
            record?.packagingType ??
            record?.packaging_type ??
            record?.packagingSubcategory ??
            record?.packaging_subcategory;
          if (raw == null || raw === '') return '—';
          const meta = normalizeShipmentMetaFromRecord(record);
          if (meta.packagingSubcategory) {
            const opt = SHIPMENT_PACKAGING_OPTIONS.find((o) => o.value === meta.packagingSubcategory);
            if (opt) return opt.label;
          }
          return String(raw);
        },
      },
      {
        title: (
          <Tooltip title="Pre-Shipment Inspection form status">
            <span className="ship-th-with-hint">PSI</span>
          </Tooltip>
        ),
        key: 'psi',
        dataIndex: 'psi',
        width: 88,
        render: (_, record) => renderStatusDot(record, 'Pre-Shipment Inspection'),
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-status' }),
      },
      {
        title: (
          <Tooltip title="Site Goods Received form status">
            <span className="ship-th-with-hint">SGR</span>
          </Tooltip>
        ),
        key: 'sgr',
        dataIndex: 'sgr',
        width: 88,
        render: (_, record) => renderStatusDot(record, 'Site Goods Received'),
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-status' }),
      },
      {
        title: 'Actions',
        key: 'action',
        dataIndex: 'action',
        width: 220,
        fixed: 'right',
        render: (text, record) => {
          const readOnly = isShipmentReadOnly();
          return (
            <div className="ship-actions-cell">
              <Tooltip title="View shipment">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="View shipment"
                  onClick={() => handleEyeClick(record)}
                >
                  <Eye size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="Edit shipment"
                  disabled={readOnly}
                  onClick={() => {
                    if (!readOnly) onShowEditModel(record);
                  }}
                >
                  <Pencil size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  type="button"
                  className="ship-action-btn ship-action-btn--delete"
                  aria-label="Delete shipment"
                  disabled={readOnly}
                  onClick={() => {
                    if (!readOnly) showDeleteModal(record);
                  }}
                >
                  <Trash2 size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Documents">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="Shipment documents"
                  onClick={() => onUpdateFileClick(record)}
                >
                  <FileText size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
            </div>
          );
        },
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-actions' }),
        onCell: () => ({ className: 'shipment-td-actions' }),
      },
    ],
    [isShipmentReadOnly, current, currentPagesize]
  );

  const columnsFile = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      align: 'left',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'left', height: '100%' }}>{text}</div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'edit_delete',
      key: 'edit_delete',
      align: 'center',
      render: (text, record) => {
        return (
          <div>
            <Tooltip title="Download">
              <a
                href={`${AWS_IMAGE_URL}/${record.fileKey}`}
                className="shipment-download-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download file"
              >
                <Download size={18} strokeWidth={2} />
              </a>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    if (rowData && rowData.length == 0) OnfetchData();
  }, []);

  useEffect(() => {
    if (shipmentData && shipmentData.length > 0) setRowData(shipmentData);
  }, [shipmentData]);

  const OnfetchData = async (page = 1, size = currentPagesize) => {
    setCurrent(page);
    setProjectIdValue(selectedProject.id);
    const obj = {
      pageSize: size,
      pageNumber: page,
      projectId: selectedProject.id,
    };
    await OnGetShipmentsData(obj);
  };

  const onShowEditModel = (record) => {
    setEditRecord(record);
    setIsEdit(true);
    setShowAddModel(true);
  };

  const onUpdateFileClick = (data) => {
    if (data) {
      setSelectedData(data);
      setIsFileModalVisible(true);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedData(record);
    setIsDeleteModalVisible(true);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipments(selectedData);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };

  const handleBulkUploadCancel = () => {
    setBulkUploadModalVisible(false);
    setBulkFileList([]);
  };

  /** Close Modal on close of table details */
  const handleCancelTable = () => {
    setIsTableModelVisible(false);
    setShowFormModel(false);
  };
  /**Open checks table / Modal on view of checks (double click on row disabled)  */
  const handleEyeClick = async (record) => {
    setSelectedData(record);
    setIsTableModelVisible(true);
    setShowFormModel(true);
  };

  const onSearchEquip = async (event) => {
    setCurrent(1);
    if (event.target.value) {
      setSearchKeyExist(event.target.value);
      await OnGetShipmentsData({
        projectId: selectedProject.id,
        searchKey: event.target.value,
      });
    } else {
      setSearchKeyExist('');
      await OnGetShipmentsData({
        projectId: selectedProject.id,
        pageSize: currentPagesize,
        pageNumber: 1,
      });
    }
  };

  const toAntUploadFile = (file) => ({
    uid: file.uid || `file-${file.name}-${Date.now()}`,
    name: file.name,
    status: 'done',
    originFileObj: file.originFileObj != null ? file.originFileObj : file,
  });

  const getRawFile = (item) => item?.originFileObj || item;

  // File upload functions
  const fileProps = {
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      setFileList((prev) => [...prev, toAntUploadFile(file)]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  // Bulk upload file props
  const bulkFileProps = {
    multiple: true,
    fileList: bulkFileList,
    beforeUpload: (file) => {
      setBulkFileList((prev) => [...prev, toAntUploadFile(file)]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setBulkFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  const uploadFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (fileList && fileList.length > 0) {
        const fileObjs = fileList.map((item) => {
          const file = getRawFile(item);
          return {
            fileName: file.name.replace(/\(\d+\)/g, '').trim(),
            contentType: file.type,
          };
        });

        // Get signed URLs for multiple files using the shipment-specific endpoint
        const signedUrlRequests = fileObjs.map((fileObj) =>
          axiosConfig.get(
            `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
              fileObj.fileName
            )}&contentType=${encodeURIComponent(fileObj.contentType)}`
          )
        );

        const signedUrlResponses = await Promise.all(signedUrlRequests);

        if (signedUrlResponses.every((res) => res.status === 200)) {
          for (let i = 0; i < signedUrlResponses.length; i++) {
            const data = signedUrlResponses[i].data.data;
            const file = getRawFile(fileList[i]);

            if (file && data) {
              try {
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({
                    fileKey: data.fileKey,
                    fileName: data.fileName,
                  });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setFileList([]);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
      return []; // Return empty array on error to prevent "not iterable" error
    }
  };

  const handleUploadFiles = async () => {
    if (fileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setUploadLoading(true);
    try {
      const uploadedFiles = await uploadFilesToS3();

      if (uploadedFiles.length > 0) {
        // No transformation needed, uploadedFiles already has fileKey and fileName
        let existingDocuments = selectedData.document || [];
        const newDocuments = [...existingDocuments, ...uploadedFiles];

        const updateData = {
          id: selectedData.pihShipmentId,
          data: {
            document: newDocuments,
            // Only pass document field, keep other fields unchanged
          },
        };

        // Import the update function
        const { OnAddorUpdateShipmentsDashboard } = await import(
          '../../redux/ShipmentDashboardDetails/saga'
        );
        const res = await OnAddorUpdateShipmentsDashboard(updateData, true);

        if (res) {
          notification.success({
            message: 'Files uploaded successfully.',
          });
          await OnfetchData();
          setIsFileModalVisible(false);
        } else {
          notification.error({
            message: 'Failed to update files.',
          });
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Bulk upload function for shipment documents
  const uploadBulkFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (bulkFileList && bulkFileList.length > 0) {
        const fileObjs = bulkFileList.map((item) => {
          const file = getRawFile(item);
          return {
            fileName: file.name.replace(/\(\d+\)/g, '').trim(),
            contentType: file.type,
          };
        });

        // Get signed URLs for multiple files using the shipment-specific endpoint
        const signedUrlRequests = fileObjs.map((fileObj) =>
          axiosConfig.get(
            `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
              fileObj.fileName
            )}&contentType=${encodeURIComponent(fileObj.contentType)}`
          )
        );

        const signedUrlResponses = await Promise.all(signedUrlRequests);

        if (signedUrlResponses.every((res) => res.status === 200)) {
          for (let i = 0; i < signedUrlResponses.length; i++) {
            const data = signedUrlResponses[i].data.data;
            const file = getRawFile(bulkFileList[i]);

            if (file && data) {
              try {
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({
                    fileKey: data.fileKey,
                    fileName: data.fileName,
                  });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setBulkFileList([]);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading bulk files:', error);
      return []; // Return empty array on error to prevent "not iterable" error
    }
  };

  const handleBulkUploadFiles = async () => {
    if (bulkFileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setBulkUploadLoading(true);
    try {
      const uploadedFiles = await uploadBulkFilesToS3();

      if (uploadedFiles.length > 0) {
        // No transformation needed, uploadedFiles already has fileKey and fileName
        // For bulk upload, you might want to show a modal to select which shipment each file belongs to
        // For now, we'll just show success message
        notification.success({
          message: `${uploadedFiles.length} files uploaded successfully.`,
        });
        // TODO: Add logic to associate files with specific shipments
        setBulkUploadModalVisible(false);
        await OnfetchData(); // Refresh the data
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const readOnly = isShipmentReadOnly();

  return (
    <div className="modern-shipment-page">
      <div className="ship-header">
        <div className="ship-header-left">
          <div className="ship-header-icon">
            <Truck size={24} color="#2563eb" />
          </div>
          <div className="ship-header-titles">
            <h1 className="ship-title">Shipments</h1>
            <p className="ship-subtitle">List and manage shipment numbers for this project.</p>
          </div>
        </div>
        <div className="ship-header-actions">
          <CrceButton
            label="Bulk upload documents"
            variant="tertiary"
            size="small"
            icon={UploadIcon}
            onClick={() => !readOnly && setBulkUploadModalVisible(true)}
            disabled={readOnly}
          />
          <CrceButton
            label="Add new"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={() => {
              if (!readOnly) {
                setEditRecord('');
                setIsEdit(false);
                setShowAddModel(true);
              }
            }}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="ship-toolbar">
        <div className="ship-search-wrap">
          <Search size={16} className="ship-search-icon" />
          <input
            type="text"
            className="ship-search-input"
            placeholder="Search shipments…"
            value={searchKeyExist}
            onChange={onSearchEquip}
            aria-label="Search shipments"
          />
        </div>
      </div>

      <div className="ship-table-shell">
        <Table
          columns={columns}
          dataSource={rowData}
          className="shipment-dashboard-table"
          rowKey={(r) => r.pihShipmentId ?? r.id ?? r.shipmentNumber}
          scroll={{ x: 1320, y: 'calc(100vh - 320px)' }}
          pagination={
            shipmentDataCount > 0
              ? {
                  position: ['bottomCenter'],
                  className: 'heat-profile-pagination',
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} shipments`,
                  total: shipmentDataCount,
                  pageSize: currentPagesize,
                  current,

                  onChange: async (pageNumber, pageSize) => {
                    setCurrent(pageNumber);
                    setCurrentPagesize(pageSize);
                    await OnGetShipmentsData({
                      projectId: projectIdValue,
                      pageSize,
                      pageNumber,
                    });
                  },
                }
              : false
          }
          loading={getProjectTableLoadingConfig(shipmentDataLoading)}
        />
      </div>

      {showAddModel && (
        <AddNewShipments
          showAddModel={showAddModel}
          setShowAddModel={setShowAddModel}
          setIsEdit={setIsEdit}
          setEditRecord={setEditRecord}
          editRecord={editRecord}
          isEdit={isEdit}
          OnfetchData={OnfetchData}
          selectedProject={selectedProject}
          adminShipmentTypesList={adminShipmentTypesList}
        />
      )}

      {showFormModel && (
        <ShipmentLogs
          showFormModel={showFormModel}
          setShowFormModel={setShowFormModel}
          selectedData={selectedData}
        />
      )}

      {isDeleteModalVisible &&
        selectedData &&
        typeof selectedData === 'object' &&
        (selectedData.pihShipmentId != null || selectedData.id != null) && (
          <div
            className="mc-dpr-modal-backdrop"
            role="presentation"
            onClick={() => !deleteLoading && handleCancel()}
          >
            <div
              className="mc-dpr-modal mc-dpr-modal--narrow mc-dpr-modal--delete"
              role="dialog"
              aria-labelledby="ship-delete-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="ship-delete-title" className="mc-dpr-modal-title">
                  Delete shipment
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={deleteLoading}
                  onClick={handleCancel}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mc-dpr-modal-body">
                <div className="mc-dpr-modal-delete-body">
                  <p className="mc-dpr-modal-delete-lead">
                    Deleting a shipment removes its shipment number and related information.
                  </p>
                  <p className="mc-dpr-modal-delete-target">
                    {selectedData.shipmentTypeName} — {selectedData.shipmentNumber}
                  </p>
                  <p className="mc-dpr-modal-delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Cancel"
                  variant="tertiary"
                  size="medium"
                  onClick={handleCancel}
                  disabled={deleteLoading}
                />
                <span className="mc-delete-confirm-wrap">
                  <CrceButton
                    label={deleteLoading ? 'Deleting…' : 'Delete'}
                    variant="primary"
                    size="medium"
                    onClick={handleOk}
                    disabled={deleteLoading}
                  />
                </span>
              </div>
            </div>
          </div>
        )}

      {isFileModalVisible &&
        selectedData &&
        typeof selectedData === 'object' && (
          <div className="mc-dpr-modal-backdrop" role="presentation">
            <div
              className="mc-dpr-modal mc-dpr-modal--large"
              role="dialog"
              aria-labelledby="ship-files-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="ship-files-title" className="mc-dpr-modal-title">
                  Shipment documents
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={uploadLoading}
                  onClick={() => {
                    setIsFileModalVisible(false);
                    setFileList([]);
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mc-dpr-modal-body mc-dpr-modal-files-body">
                <Table
                  columns={columnsFile}
                  dataSource={selectedData.document || []}
                  pagination={false}
                  className="shipment-inner-file-table"
                  locale={{ emptyText: 'No documents uploaded yet' }}
                />
                <div className="mc-dpr-modal-intro" style={{ marginTop: '20px' }}>
                  <strong style={{ color: '#374151' }}>Add files</strong>
                  <br />
                  Excel or PDF, up to 100 files.
                </div>
                <div className="ship-modal-upload-wrap">
                  <Upload {...fileProps} className="ship-modal-upload">
                    <CrceButton
                      label="Select files"
                      variant="tertiary"
                      size="medium"
                      icon={UploadIcon}
                      disabled={fileList.length >= 100 || uploadLoading}
                    />
                  </Upload>
                </div>
                {fileList.length > 0 && (
                  <div className="ship-upload-toolbar">
                    <CrceButton
                      label={uploadLoading ? 'Uploading…' : 'Upload files'}
                      variant="primary"
                      size="medium"
                      onClick={handleUploadFiles}
                      disabled={uploadLoading}
                    />
                    <CrceButton
                      label="Clear selection"
                      variant="tertiary"
                      size="medium"
                      onClick={() => setFileList([])}
                      disabled={uploadLoading}
                    />
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      {fileList.length} file(s) selected
                    </span>
                  </div>
                )}
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Close"
                  variant="tertiary"
                  size="medium"
                  onClick={() => {
                    setIsFileModalVisible(false);
                    setFileList([]);
                  }}
                  disabled={uploadLoading}
                />
              </div>
            </div>
          </div>
        )}

      {bulkUploadModalVisible && (
        <div
          className="mc-dpr-modal-backdrop"
          role="presentation"
          onClick={() => !bulkUploadLoading && handleBulkUploadCancel()}
        >
          <div
            className="mc-dpr-modal mc-dpr-modal--medium ship-bulk-modal"
            role="dialog"
            aria-labelledby="ship-bulk-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="ship-bulk-title" className="mc-dpr-modal-title">
                Bulk upload documents
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close"
                aria-label="Close"
                disabled={bulkUploadLoading}
                onClick={handleBulkUploadCancel}
              >
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body ship-bulk-modal-body">
              <p className="mc-dpr-modal-intro">
                Select multiple Excel or PDF files (up to 100). They will be stored as shipment
                documents.
              </p>
              <div className="mc-dpr-modal-upload-wrap ship-modal-upload-wrap">
                <Upload {...bulkFileProps} className="ship-modal-upload">
                  <CrceButton
                    label="Select files"
                    variant="tertiary"
                    size="medium"
                    icon={UploadIcon}
                    disabled={bulkFileList.length >= 100 || bulkUploadLoading}
                  />
                </Upload>
              </div>
              {bulkFileList.length > 0 && (
                <div className="ship-bulk-selected-summary" role="status">
                  {bulkFileList.length} file{bulkFileList.length === 1 ? '' : 's'} selected
                </div>
              )}
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleBulkUploadCancel}
                disabled={bulkUploadLoading}
              />
              <CrceButton
                label={bulkUploadLoading ? 'Uploading…' : 'Upload files'}
                variant="primary"
                size="medium"
                onClick={handleBulkUploadFiles}
                disabled={bulkUploadLoading || bulkFileList.length === 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ ShipmentReducer, ShipmentDashBoardReducer, equip }) => {
  const shipmentsTypes = get(ShipmentReducer, 'shipmentData', []);
  const shipmentTypesLoading = get(ShipmentReducer, 'isShipmentTypeLoading', false);
  const shipmentTypesCount = get(ShipmentReducer, 'isShipmentTypeLoading', false);

  const shipmentData = get(ShipmentDashBoardReducer, 'shipmentListData', []);
  const shipmentDataCount = get(ShipmentDashBoardReducer, 'shipmentsCount', false);
  const shipmentDataLoading = get(ShipmentDashBoardReducer, 'isShipmentsLoading', false);

  const userAccessList = get(equip, 'userAccessList', []);
  const projectTeamMembersAccessList = get(equip, 'projectTeamMembersAccessList', []);

  return {
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    userAccessList,
    projectTeamMembersAccessList,
  };
};

const mapDispatchToProps = {
  onGetShipmentTypes: fetchShipmentTypeDetails,
  OnGetShipmentsData: fetchShipmentDashBoardDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentDashBoardPage);
